import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Menu, MenuItem, TextField, Button } from '@mui/material';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import FavoriteIcon from '@mui/icons-material/Favorite';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';

const WhatsAppChatBubble = ({ message, contactName, onDelete, onEdit, onReaction }) => {
  const { text, sender, timestamp, type, reactions = [], imageUrl, waveform } = message;
  const isMe = sender === 'me';
  
  // State for menu handling
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  
  // State for editing
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(text);
  
  // State for double tap reaction
  const [lastTap, setLastTap] = useState(0);
  
  // State for visible reactions (for delayed rendering)
  const [visibleReactions, setVisibleReactions] = useState([]);
  
  // Effect to handle delayed reaction rendering
  useEffect(() => {
    if (reactions && reactions.length > 0) {
      // Clear visible reactions when reactions change completely
      if (visibleReactions.length > reactions.length) {
        setVisibleReactions([]);
      }
      
      // Add reactions one by one with delay
      reactions.forEach((reaction, index) => {
        const alreadyVisible = visibleReactions.some(r => r.id === reaction.id);
        
        if (!alreadyVisible) {
          const delay = 1000 + (index * 400); // 1 second initial delay + staggered delay for each reaction
          setTimeout(() => {
            setVisibleReactions(prev => [...prev, reaction]);
          }, delay);
        }
      });
    } else {
      setVisibleReactions([]);
    }
  }, [reactions, visibleReactions]);
  
  // Function to render text with formatting support
  const renderText = (text) => {
    if (!text) return '';
    
    // Process bold text: *bold*
    let formattedText = text.replace(/\*(.*?)\*/g, '<strong>$1</strong>');
    
    // Process italic text: _italic_
    formattedText = formattedText.replace(/_(.*?)_/g, '<em>$1</em>');
    
    // Process strikethrough: ~strikethrough~
    formattedText = formattedText.replace(/~(.*?)~/g, '<del>$1</del>');
    
    // Process line breaks
    formattedText = formattedText.replace(/\n/g, '<br />');
    
    return formattedText;
  };
  
  // Handle menu click
  const handleMenuClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  
  // Handle menu close
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  // Handle message deletion
  const handleDelete = () => {
    handleClose();
    if (onDelete) {
      onDelete(message.id);
    }
  };
  
  // Handle edit button click
  const handleEditClick = () => {
    handleClose();
    setIsEditing(true);
    setEditedText(text);
  };
  
  // Handle save edit
  const handleSaveEdit = () => {
    if (onEdit && editedText.trim()) {
      onEdit(message.id, editedText);
      setIsEditing(false);
    }
  };
  
  // Handle cancel edit
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedText(text);
  };
  
  // Get emoji for reaction type
  const getReactionEmoji = (type) => {
    switch(type) {
      case 'like':
        return <ThumbUpAltIcon sx={{ fontSize: 14, color: '#128C7E' }} />;
      case 'love':
        return <FavoriteIcon sx={{ fontSize: 14, color: '#E91E63' }} />;
      case 'laugh':
        return <SentimentVerySatisfiedIcon sx={{ fontSize: 14, color: '#FFC107' }} />;
      case 'sad':
        return <SentimentVeryDissatisfiedIcon sx={{ fontSize: 14, color: '#FFC107' }} />;
      case 'wow':
        return <EmojiEmotionsIcon sx={{ fontSize: 14, color: '#FFC107' }} />;
      default:
        // If it's not one of our predefined reactions, it might be a custom emoji
        if (type && type.length <= 2) {
          // Likely an emoji character
          return <Typography sx={{ fontSize: 14 }}>{type}</Typography>;
        }
        return null;
    }
  };
  
  // Handle double tap reaction
  const handleTap = (e) => {
    e.stopPropagation();
    const currentTime = new Date().getTime();
    const tapLength = currentTime - lastTap;
    
    if (tapLength < 300 && tapLength > 0) {
      // Double tap detected
      if (onReaction) {
        // Use the sender correctly based on who's message it is
        // If it's my message, the contact reacts, and vice versa
        onReaction('like', isMe ? 'contact' : 'me');
      }
    }
    
    setLastTap(currentTime);
  };
  
  // Render reactions if present
  const renderReactions = () => {
    if (!visibleReactions || visibleReactions.length === 0) return null;
    
    // Group identical reactions to avoid duplicates
    const groupedReactions = visibleReactions.reduce((acc, reaction) => {
      const existingGroup = acc.find(group => group.type === reaction.type);
      if (existingGroup) {
        existingGroup.count += 1;
        existingGroup.senders.push(reaction.sender);
      } else {
        acc.push({
          type: reaction.type,
          count: 1,
          senders: [reaction.sender]
        });
      }
      return acc;
    }, []);
    
    return (
      <Box 
        sx={{ 
          position: 'absolute', 
          bottom: -10, 
          right: isMe ? 'auto' : 10, 
          left: isMe ? 10 : 'auto',
          bgcolor: 'white',
          borderRadius: 12,
          height: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          zIndex: 5,
          px: 0.5,
          cursor: 'pointer',
          '&:hover': {
            bgcolor: '#f5f5f5'
          },
          animation: 'fadeIn 0.3s ease-in-out',
          '@keyframes fadeIn': {
            '0%': {
              opacity: 0,
              transform: 'translateY(5px)'
            },
            '100%': {
              opacity: 1,
              transform: 'translateY(0)'
            }
          }
        }}
        onClick={(e) => {
          e.stopPropagation();
          // Toggle like reaction from the current user
          if (onReaction) {
            // Use 'me' as the sender since this is a user action
            onReaction('like', 'me');
          }
        }}
      >
        {groupedReactions.map((group, index) => (
          <Box 
            key={index} 
            sx={{ 
              mx: 0.25,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: `popIn 0.2s ease-out ${index * 0.1}s`,
              '@keyframes popIn': {
                '0%': {
                  transform: 'scale(0)'
                },
                '70%': {
                  transform: 'scale(1.2)'
                },
                '100%': {
                  transform: 'scale(1)'
                }
              }
            }}
          >
            {getReactionEmoji(group.type)}
          </Box>
        ))}
      </Box>
    );
  };

  // Render image message
  if (type === 'image') {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: isMe ? 'flex-end' : 'flex-start',
          mb: 1,
          position: 'relative',
        }}
      >
        <Box
          sx={{
            bgcolor: isMe ? '#dcf8c6' : 'white',
            color: 'black',
            p: 1,
            borderRadius: 2,
            maxWidth: '70%',
            position: 'relative',
            '&:hover .message-options': {
              opacity: 0.7,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              [isMe ? 'right' : 'left']: -10,
              width: 0,
              height: 0,
              borderTop: '10px solid transparent',
              borderBottom: '10px solid transparent',
              [isMe ? 'borderRight' : 'borderLeft']: isMe ? '10px solid #dcf8c6' : '10px solid white',
              transform: isMe ? 'rotate(180deg)' : 'none',
            }
          }}
          onClick={handleTap}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', height: 0, position: 'absolute', top: 0, right: 5, zIndex: 1 }}>
            <IconButton 
              size="small" 
              onClick={handleMenuClick}
              className="message-options"
              sx={{ 
                opacity: 0, 
                transition: 'opacity 0.2s',
                padding: '2px',
                ml: 'auto'
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem onClick={handleDelete} dense>
                <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                Delete
              </MenuItem>
            </Menu>
          </Box>
          
          <Box sx={{ maxWidth: '100%', overflow: 'hidden' }}>
            <img 
              src={imageUrl} 
              alt="Shared content" 
              style={{ 
                maxWidth: '100%', 
                borderRadius: '4px',
                display: 'block'
              }} 
            />
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 0.5, gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {timestamp}
            </Typography>
            {isMe && (
              <DoneAllIcon sx={{ fontSize: 14, color: '#34B7F1' }} />
            )}
          </Box>
          {renderReactions()}
        </Box>
      </Box>
    );
  }

  // Render audio message
  if (type === 'audio') {
    const audioWaveform = waveform || Array(40).fill(0.5);
    
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: isMe ? 'flex-end' : 'flex-start',
          mb: 1,
          position: 'relative',
        }}
      >
        <Box
          sx={{
            bgcolor: isMe ? '#dcf8c6' : 'white',
            color: 'black',
            p: 1,
            px: 2,
            borderRadius: 2,
            maxWidth: '80%',
            position: 'relative',
            '&:hover .message-options': {
              opacity: 0.7,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              [isMe ? 'right' : 'left']: -10,
              width: 0,
              height: 0,
              borderTop: '10px solid transparent',
              borderBottom: '10px solid transparent',
              [isMe ? 'borderRight' : 'borderLeft']: isMe ? '10px solid #dcf8c6' : '10px solid white',
              transform: isMe ? 'rotate(180deg)' : 'none',
            }
          }}
          onClick={handleTap}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', height: 0, position: 'absolute', top: 0, right: 5, zIndex: 1 }}>
            <IconButton 
              size="small" 
              onClick={handleMenuClick}
              className="message-options"
              sx={{ 
                opacity: 0, 
                transition: 'opacity 0.2s',
                padding: '2px',
                ml: 'auto'
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem onClick={handleDelete} dense>
                <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                Delete
              </MenuItem>
            </Menu>
          </Box>
          
          <IconButton size="small" sx={{ color: '#128C7E', p: 0.5 }}>
            <PlayArrowIcon fontSize="small" />
          </IconButton>
          
          <Box sx={{ flex: 1, minWidth: 150 }}>
            <svg width="100%" height="16" viewBox={`0 0 ${audioWaveform.length * 4} 16`}>
              {audioWaveform.map((h, i) => (
                <rect
                  key={i}
                  x={i * 4}
                  y={16 - h * 14}
                  width="2.5"
                  height={h * 14}
                  fill="#128C7E"
                  rx="1"
                />
              ))}
            </svg>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {timestamp}
            </Typography>
            {isMe && (
              <DoneAllIcon sx={{ fontSize: 14, color: '#34B7F1' }} />
            )}
          </Box>
          {renderReactions()}
        </Box>
      </Box>
    );
  }

  // Render reaction message
  if (type === 'reaction') {
    return null; // Don't render reaction messages anymore
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isMe ? 'flex-end' : 'flex-start',
        mb: 1,
        position: 'relative',
      }}
    >
      <Box
        sx={{
          bgcolor: isMe ? '#dcf8c6' : 'white',
          color: 'black',
          p: 1,
          px: 2,
          pt: 0.5,
          borderRadius: 2,
          maxWidth: '70%',
          position: 'relative',
          '&:hover .message-options': {
            opacity: 0.7,
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            [isMe ? 'right' : 'left']: -10,
            width: 0,
            height: 0,
            borderTop: '10px solid transparent',
            borderBottom: '10px solid transparent',
            [isMe ? 'borderRight' : 'borderLeft']: isMe ? '10px solid #dcf8c6' : '10px solid white',
            transform: isMe ? 'rotate(180deg)' : 'none',
          }
        }}
        onClick={handleTap}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', height: 0, position: 'absolute', top: -5, right: -5, zIndex: 1 }}>
          <IconButton 
            size="small" 
            onClick={handleMenuClick}
            className="message-options"
            sx={{ 
              opacity: 0, 
              transition: 'opacity 0.2s',
              padding: '2px',
              ml: 'auto'
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={handleEditClick} dense>
              <EditIcon fontSize="small" sx={{ mr: 1 }} />
              Edit
            </MenuItem>
            <MenuItem onClick={handleDelete} dense>
              <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
              Delete
            </MenuItem>
          </Menu>
        </Box>
        {isEditing ? (
          <Box sx={{ mt: 1 }}>
            <TextField
              fullWidth
              multiline
              size="small"
              value={editedText}
              onChange={(e) => setEditedText(e.target.value)}
              variant="outlined"
              autoFocus
              sx={{ mb: 1 }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
              <Button size="small" variant="outlined" onClick={handleCancelEdit}>Cancel</Button>
              <Button size="small" variant="contained" onClick={handleSaveEdit}>Save</Button>
            </Box>
          </Box>
        ) : (
          <Typography 
            variant="body1" 
            sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            dangerouslySetInnerHTML={{ __html: renderText(text) }}
          />
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 0.5, gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {timestamp}
          </Typography>
          {isMe && (
            <DoneAllIcon sx={{ fontSize: 14, color: '#34B7F1' }} />
          )}
        </Box>
        {renderReactions()}
      </Box>
    </Box>
  );
};

export default WhatsAppChatBubble;
